import React, { useContext } from 'react';
import { Box, IconButton, Table, TableBody, TableCell, TableRow, useTheme } from '@mui/material';
import { CardHeader, CardSection } from '@europrocurement/flexy-components';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { Link } from 'react-router-dom';
import {
    DossierCompanyFormality,
    DossierDateCreation,
    DossierNotifications,
    DossierReferences,
    DossierRoutingContext,
    DossierStatus,
    DossierStepCompletion,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { DossierOpenSearch, NestedDossierFormaliteOpenSearch } from '@europrocurement/l2d-domain';

type DossiersCollectionProps = {
    collectionNumber: string;
    dossiers: DossierOpenSearch[];
    source: 'FRONT' | 'BACK';
};

const DossiersCollection: React.FunctionComponent<DossiersCollectionProps> = function ({
    collectionNumber,
    dossiers,
    source,
}) {
    const { getDossierRoute } = useContext(DossierRoutingContext);
    const { palette } = useTheme();

    if (!dossiers || dossiers.length === 0) return null;

    const titleSx = {
        fontSize: '20px',
        lineHeight: '28px',
    };
    const headingSx = {
        borderLeftWidth: '2px',
        minHeight: 'auto',
    };

    const linkStyle = {
        borderRadius: '5px',
        display: 'flex',
        margin: 'auto',
        justifyContent: 'center',
        alignItems: 'center',
        width: '40px',
        height: '40px',
        '&:hover': {
            backgroundColor: palette.action.hover,
        },
    };
    const linkIconStyle = {
        fontSize: '16px',
    };

    const cardHeader = (
        <Box>
            <CardHeader
                title={`Formalités de la commande ${collectionNumber}`}
                titleSx={titleSx}
                sx={headingSx}
            />
        </Box>
    );

    const items = dossiers
        .sort((a, b) => Number(a.deleted) - Number(b.deleted))
        .map((dossier, index) => {
            const cellSx = index < dossiers.length - 1 ? {} : { border: 'none' };

            return (
                <TableRow
                    key={dossier.numero}
                    sx={{ opacity: dossier.deleted ? 0.4 : 1 }}
                >
                    <TableCell sx={cellSx}>
                        <DossierReferences
                            itemReference={dossier.numero as string}
                            companyReference={dossier.societeRef as string}
                            direction="column"
                        />
                    </TableCell>
                    <TableCell sx={cellSx}>
                        <DossierCompanyFormality
                            libelle={dossier.clientOpereLibelle as string}
                            demo={dossier.demo}
                            formalites={dossier.formalites?.map(
                                (formalite) =>
                                    ({
                                        id: formalite?.id as number,
                                        libelle: formalite?.libelle as string,
                                    }) as NestedDossierFormaliteOpenSearch,
                            )}
                        />
                    </TableCell>
                    <TableCell sx={cellSx}>
                        <DossierStepCompletion
                            step={dossier.stepLabel as string}
                            currentCompletion={dossier.stepCompletion as number}
                        />
                    </TableCell>
                    <TableCell sx={cellSx}>
                        {dossier.dateCreation && (
                            <DossierDateCreation date={new Date(dossier.dateCreation)} />
                        )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                        <DossierNotifications
                            missingDocumentsCount={dossier.missingRequiredDocumentsCount as number}
                            totalDocCount={dossier.documentsCount as number}
                        />
                    </TableCell>
                    <TableCell sx={cellSx}>
                        <DossierStatus
                            statutId={dossier.statutId as number}
                            label={dossier.statutLibelle as string}
                            source={source}
                        />
                    </TableCell>
                    <TableCell sx={cellSx}>
                        <Box>
                            <Link
                                to={dossier.deleted ? '#' : getDossierRoute(dossier.id as number)}
                                style={{
                                    textDecoration: 'none',
                                    ...(dossier.deleted && { pointerEvents: 'none' }),
                                }}
                            >
                                <IconButton
                                    sx={{
                                        ...linkStyle,
                                        cursor: dossier.deleted ? 'default' : 'pointer',
                                    }}
                                >
                                    <FaOptionIcon
                                        icon={['fasl', 'chevron-right']}
                                        sx={linkIconStyle}
                                        size="xs"
                                    />
                                </IconButton>
                            </Link>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        });

    return (
        <CardSection
            header={cardHeader}
            sxContent={{ padding: '24px 32px' }}
        >
            <Table>
                <TableBody>{items}</TableBody>
            </Table>
        </CardSection>
    );
};

export default DossiersCollection;
